'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { AlertCircle, RotateCcw, Home } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import Logo from '@/components/custom/Logo'

interface ErrorStateProps {
    error: Error & { digest?: string }
    reset: () => void
    title?: string
    description?: string
    homeHref?: string
}

export default function ErrorState({
    error,
    reset,
    title = 'Something went wrong!',
    description = 'We apologize for the inconvenience. An unexpected error occurred.',
    homeHref = '/'
}: ErrorStateProps) {
    useEffect(() => {
        console.error(error)
    }, [error])

    return (
        <div className='flex items-center justify-center p-4 bg-background/50 backdrop-blur-sm h-full'>
            <Card className='w-full max-w-md text-center border-none shadow-2xl bg-card/50 backdrop-blur-md'>
                <CardHeader className='flex flex-col items-center space-y-4 pb-2'>
                    <div className='p-3 bg-primary/5 rounded-full ring-1 ring-primary/10 mb-2'>
                        <Logo />
                    </div>
                    <div className='space-y-2'>
                        <CardTitle className='text-2xl font-bold tracking-tight'>{title}</CardTitle>
                        <CardDescription className='text-base'>{description}</CardDescription>
                    </div>
                </CardHeader>
                <CardContent className='pb-6'>
                    <div className='mx-auto w-full max-w-75 p-4 rounded-lg bg-destructive/5 border border-destructive/10 text-destructive text-sm font-medium flex items-center gap-3 justify-center'>
                        <AlertCircle className='h-4 w-4 shrink-0' />
                        <span className=''>{error.message || 'Unknown error'}</span>
                    </div>
                    {error.digest && (
                        <p className='mt-2 text-xs text-muted-foreground font-mono'>Error ID: {error.digest}</p>
                    )}
                </CardContent>
                <CardFooter className='flex flex-col sm:flex-row gap-3 justify-center pb-8'>
                    <Button onClick={reset} variant='default' size='lg' className='w-full sm:w-auto gap-2'>
                        <RotateCcw className='h-4 w-4' />
                        Try Again
                    </Button>
                    <Button variant='outline' size='lg' asChild className='w-full sm:w-auto gap-2'>
                        <Link href={homeHref}>
                            <Home className='h-4 w-4' />
                            Go Home
                        </Link>
                    </Button>
                </CardFooter>
            </Card>
        </div>
    )
}
